import {Alert, Badge, Button, Card, Col, Divider, Form, message, Modal, Row, Spin, Upload} from 'antd';
import React, {
    forwardRef,
    JSXElementConstructor,
    ReactElement,
    ReactNode,
    ReactPortal,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'react';
import {DownloadOutlined, ExclamationCircleOutlined, FilePdfOutlined, PlusOutlined,} from "@ant-design/icons";
import './style.scss'
import documentService from "../../../service/documentService";
import {FileProperty} from "../../common/documentviewer/FileProperty";
import {
    MSG_ALLOWED_IMAGE_TYPES_ERROR,
    MSG_FAILED_TO_DELETE_DOCUMENTS_ERROR,
    MSG_FAILED_TO_FETCH_DOCUMENTS_ERROR,
    MSG_FAILED_TO_PREVIEW_DOCUMENTS_ERROR,
    MSG_FAILED_TO_UPLOAD_DOCUMENTS_ERROR
} from "../../../util/Messages";
import ErrorHandler from "../../../util/ErrorHandler";
import {
    ALLOWED_IMAGE_TYPE_EXTENSION,
    ALLOWED_IMAGE_TYPES,
    ALLOWED_IMAGE_TYPES_TEXT,
    BASKET_STATUS_SIGNATURE_BASKET,
    DOC_CODE_DRIVING_LICENSE,
    DOC_CODE_NIC,
    DOC_CODE_PASSPORT,
    DOC_CODE_PROFILE_PIC,
    DOC_CODE_SIGNATURE_DOC,
    DOC_CODE_SIGNATURE_DOC_TEMP,
    DOCUMENT_TYPES,
    IDENTITY_DOC_TYPE_CODE_LIST
} from "../../../util/Constants";
import PreviewModalComp from "../../common/preview/PreviewModalComp";
import Draggable, {DraggableData, DraggableEvent} from "react-draggable";
import {DeleteOutlined, EyeOutlined} from "@ant-design/icons/lib";
import {DefaultDocumentDto} from "../../../dto/DefaultDocumentDto";
import {toNumber} from "lodash";
import modal from "antd/es/modal";

interface DocumentsCompare {
    documentTypeName: string
    captured: any
    uploaded: any
}

export interface DocumentViewRef {
    getCanVerify: () => {
        canVerify: boolean;

    };
}

interface DocumentViewProps {
    jobNo: number;
    identityDocumentTypeCode: string;
    addDocumentWindow: (fileProperty: FileProperty, contentType: string) => void;
    capturedDoc: boolean;
    basketStatus: string;
    uploadingStatus: (bool: boolean) => void;
}


const DocumentView = forwardRef<DocumentViewRef, DocumentViewProps>(
    ({jobNo, identityDocumentTypeCode, addDocumentWindow, capturedDoc, basketStatus, uploadingStatus}, ref) => {
        const uploadFileSize: number | undefined = process.env.UPLOAD_FILE_SIZE ? toNumber(process.env.UPLOAD_FILE_SIZE) : 4;

        const [documentListCaptured, setDocumentListCaptured] = useState<Array<any>>([]);
        const [documentListUploaded, setDocumentListUploaded] = useState<Array<any>>([]);
        const [documentsList, setDocumentList] = useState<DocumentsCompare[]>([]);
        const [docUrl, setDocUrl] = useState("");
        const [docName, setDocName] = useState("");
        const [docType, setDocType] = useState("IMAGE");
        const [isDocLoading, setIsDocLoading] = useState(false);

        const [disabled, setDisabled] = useState(true);
        const [bounds, setBounds] = useState({left: 0, top: 0, bottom: 0, right: 0});
        const draggleRef = useRef<HTMLDivElement | null>(null);
        const [isCompareModalOpen, setCompareModalOpen] = useState(false);
        const [capturedDocumentList, setCapturedDocumentList] = useState<DefaultDocumentDto[]>()
        const [isSpinnerLoading, setSpinnerLoading] = useState<boolean>(false)

        const [compareModalDetails, setCompareModalDetails] = useState<{
            capturedDocUrl: string
            customerUploadedDocUrl: string
            documentType: string
        }>({
            capturedDocUrl: "",
            customerUploadedDocUrl: "",
            documentType: ""
        })

        useEffect(() => {
            loadDocList()
            return () => {
                setDocumentListCaptured([]);
                setDocumentListUploaded([]);
                setDocumentList([]);
            };

        }, []);

        useImperativeHandle(ref, () => ({
            getCanVerify: () => ({
                canVerify: checkIsCanVerify()
            }),
        }));

        const checkIsCanVerify = () => {
            return documentsList.every((doc) => {
                if (COMPARE_APPLICABLE_DOCS.includes(doc.captured.documentTypeCode)) {
                    return doc.captured?.uploadDocumentDetailDto !== null;
                }
                // Return true for documents not applicable for comparison
                return true;
            });
        };


        const [open, setOpen] = useState(false);

        const loadDocList = () => {
            setIsDocLoading(true)
            uploadingStatus(true)
            getUploadedDocList().then(r => {
                getCapturedDocList(r)
            })

        }
        const getUploadedDocList = () => {
            return documentService.getUploadDocumentListByType(jobNo,
                basketStatus === BASKET_STATUS_SIGNATURE_BASKET ? [DOC_CODE_SIGNATURE_DOC_TEMP] :
                    IDENTITY_DOC_TYPE_CODE_LIST).then(res => {
                if (res.data.status == 'SUCCESS') {

                    setDocumentListUploaded(res.data.results);
                    setIsDocLoading(false)
                    uploadingStatus(false)
                    return res.data.results
                }
            }, (reason) => {
                setIsDocLoading(false)
                uploadingStatus(false)
                ErrorHandler.handleError(reason);
            });
        }
        const getCapturedDocList = (uploadedDocList: any) => {
            setIsDocLoading(true)
            documentService.getCapturedDocumentListByType(jobNo, IDENTITY_DOC_TYPE_CODE_LIST).then(res => {
                if (res.data.status == 'SUCCESS') {
                    setDocumentListCaptured(res.data.results);
                    const capturedList = res.data.results
                    let updatedDocList: DocumentsCompare[] = []
                    uploadedDocList.map((document) => {
                        if (document.uploadDocumentDetailDto != null) {
                            let capturedDocument;
                            if (basketStatus === BASKET_STATUS_SIGNATURE_BASKET) {
                                capturedDocument = capturedList.find(capturedDoc => capturedDoc.documentTypeCode === DOC_CODE_SIGNATURE_DOC);
                            } else {
                                capturedDocument = capturedList.find(capturedDoc => capturedDoc.documentTypeId === document.documentTypeId);
                            }
                            let docCompare = {
                                documentTypeName: document.documentTypeName,
                                captured: capturedDocument,
                                uploaded: document
                            } as DocumentsCompare

                            updatedDocList.push(docCompare)
                        }

                    })
                    // add propic if uploaded is null since propic is mandatory in video verificatrion
                    if (basketStatus !== BASKET_STATUS_SIGNATURE_BASKET) {
                        const uploadedDocumentProPic = uploadedDocList.find(uploadedDoc => uploadedDoc.documentTypeCode === DOC_CODE_PROFILE_PIC);
                        if (uploadedDocumentProPic && uploadedDocumentProPic.uploadDocumentDetailDto === null) {
                            const capturedDocument = capturedList.find(capturedDoc => capturedDoc.documentTypeId === uploadedDocumentProPic.documentTypeId);

                            let docCompare = {
                                documentTypeName: uploadedDocumentProPic.documentTypeName,
                                captured: capturedDocument,
                                uploaded: uploadedDocumentProPic
                            } as DocumentsCompare

                            updatedDocList.push(docCompare)
                        }
                    }
                    //----


                    setDocumentList(updatedDocList)
                    setIsDocLoading(false)
                }
            }, (reason) => {
                setIsDocLoading(false)
                ErrorHandler.handleError(reason);
            });
        }
        const showModal = (uploadedImage: any) => {
            onPreview(uploadedImage)
        };
        const onPreview = (document: any) => {
            setDocName(document.originalDocumentName)
            setIsDocLoading(true)
            documentService.viewOriginalDocument(document.uploadDocumentDetailId).then(res => {
                setIsDocLoading(false)
                if (res.data !== null) {
                    setDocType(document.fileType)
                    setDocUrl(res.data.results)
                    setOpen(true);
                } else {
                    message.error(MSG_FAILED_TO_PREVIEW_DOCUMENTS_ERROR).then();
                }
            }, (reason) => {
                setIsDocLoading(false)
                ErrorHandler.handleError(reason);
            })

        };
        const onCompare = (documentLeft: any, documentRight: any, docType: string) => {
            setIsDocLoading(true)
            Promise.all([viewOriginalDocument(documentLeft.uploadDocumentDetailId), viewOriginalDocument(documentRight.uploadDocumentDetailId)]).then(res => {
                if (res.every((res) => res.status === 200)) {
                    setCompareModalDetails({
                        customerUploadedDocUrl: res[0].data.results,
                        capturedDocUrl: res[1].data.results,
                        documentType: docType
                    })
                    setCompareModalOpen(true);
                    setIsDocLoading(false)
                } else {
                    message.error(MSG_FAILED_TO_FETCH_DOCUMENTS_ERROR).then();
                    setCompareModalOpen(true);
                    setIsDocLoading(false)
                }
            }).catch(reason => {
                ErrorHandler.handleError(reason);
                setCompareModalOpen(false);
                setIsDocLoading(false)
            })

        };
        const viewOriginalDocument = (documentId: string) => {
            return documentService.viewOriginalDocument(documentId)
        }
        const handleCancel = (e: React.MouseEvent<HTMLElement>) => {
            setOpen(false);
        };

        const download = () => {
            let a = document.createElement('a');
            a.href = docUrl;
            a.download = docName;
            a.click();
        }
        const onStart = (_event: DraggableEvent, uiData: DraggableData) => {
            const {clientWidth, clientHeight} = window.document.documentElement;
            const targetRect = draggleRef.current?.getBoundingClientRect();
            if (!targetRect) {
                return;
            }
            setBounds({
                left: -targetRect.left + uiData.x,
                right: clientWidth - (targetRect.right - uiData.x),
                top: -targetRect.top + uiData.y,
                bottom: clientHeight - (targetRect.bottom - uiData.y),
            });
        };

        const normFile = (e: { fileList: any; }) => {
            if (Array.isArray(e)) {
                return e;
            }
            return e && e.fileList;
        };

        const configs = {
            name: 'file',
            multiple: false,
            maxCount: 1
        };

        const beforeUploadNicFront = (file: any, documentTypeId: number) => {
            return validateDocument(file, documentTypeId);
        };

        const validateDocument = (file: any, type: DOCUMENT_TYPES) => {
            setSpinnerLoading(true)
            let indexOf = ALLOWED_IMAGE_TYPES.indexOf(file.type);
            if (indexOf < 0) {

                message.error(MSG_ALLOWED_IMAGE_TYPES_ERROR);
                setSpinnerLoading(false)
                return Upload.LIST_IGNORE;
            }
            const isLt2M = file.size / 1024 / 1024 <= uploadFileSize;
            if (!isLt2M) {
                message.error(`Document must smaller than ${uploadFileSize}MB!`);
                setSpinnerLoading(false)
                return Upload.LIST_IGNORE;
            }

            const formData = new FormData();
            formData.append("files", file);

            formData.append("jobNo", jobNo as string);
            formData.append("documentTypeId", JSON.stringify(type));

            documentService.uploadManuallyCaptureDocument(formData).then(res => {
                loadDocList()
                if (res.data !== null) {
                    setSpinnerLoading(false)
                } else {
                    setSpinnerLoading(false)
                    message.error(MSG_FAILED_TO_UPLOAD_DOCUMENTS_ERROR).then();
                }
            }, (reason) => {
                setSpinnerLoading(false)
                ErrorHandler.handleError(reason);
            })
            return false;
        };

        const onRemove = (documentId: string) => {
            modal.confirm({
                title: 'Confirm',
                className: 'confirm-dialog-box',
                icon: <ExclamationCircleOutlined/>,
                content: `Are you sure?, Do you want to delete this captured document?`,
                okText: 'Yes',
                cancelText: 'No',
                okButtonProps: {style: {background: '#FF7400'}},
                onOk() {
                    setSpinnerLoading(true)
                    documentService.deleteDocumentById(documentId).then(res => {
                        if (res.data !== null) {
                            setSpinnerLoading(false)
                        } else {
                            setSpinnerLoading(false)
                            message.error(MSG_FAILED_TO_DELETE_DOCUMENTS_ERROR).then();
                        }
                        loadDocList()
                    }, (reason) => {
                        setSpinnerLoading(false)
                        ErrorHandler.handleError(reason);
                    })
                },
                onCancel() {
                    setSpinnerLoading(false)
                }
            });


            return true;
        };
        const getCompareModelBody = (documentDetail: any, isCaptured: boolean) => {
            return <Badge.Ribbon text={isCaptured ? "Captured" : "Uploaded"}
                                 color={isCaptured ? "#73d13d" : "#597ef7"}

                                 className={'doc-ribbon'}>
                <div className='utility_bill'
                     key={`item${documentDetail?.uploadDocumentDetailDto.uploadDocumentDetailId}`}>
                    <div className='utulity_bill_icon_div'>
                        <div>
                            {
                                documentDetail?.uploadDocumentDetailDto.fileType == 'IMAGE' ?
                                    <img className='videocall_Verification_doc_image'
                                         src={documentDetail?.uploadDocumentDetailDto.thumbB64}
                                         alt=""/> :
                                    <FilePdfOutlined style={{color: "red"}}/>
                            }

                        </div>

                    </div>
                    {isCaptured ?
                        <Button className={'doc-delete-btn margin_right_5'}
                                onClick={() => onRemove(documentDetail?.uploadDocumentDetailDto.uploadDocumentDetailId)}
                                type='link'><DeleteOutlined/></Button> : <></>}
                    <Button className={'doc-view-btn'}
                            onClick={() => showModal(documentDetail.uploadDocumentDetailDto)}
                            type='link'><EyeOutlined/></Button>

                </div>
            </Badge.Ribbon>
        }
        const COMPARE_APPLICABLE_DOCS = [DOC_CODE_SIGNATURE_DOC_TEMP,
            DOC_CODE_SIGNATURE_DOC,
            DOC_CODE_PROFILE_PIC,
            DOC_CODE_PASSPORT,
            DOC_CODE_DRIVING_LICENSE, DOC_CODE_NIC]
        return (<>
            <Spin spinning={isDocLoading}>
                <div className='doc_view'>
                    {
                        documentsList.map(documentDto => {
                            return (
                                <Row gutter={16}>
                                    {
                                        documentDto.uploaded.uploadDocumentDetailDto !== null
                                        || documentDto.captured.uploadDocumentDetailDto !== null ||
                                        documentDto.captured.documentTypeCode === DOC_CODE_PROFILE_PIC ?
                                            <Col span={24}>
                                                <Divider key={`divider${documentDto.documentTypeName}`}
                                                         orientation="left" plain
                                                         orientationMargin="0" className='meeting-info-divider'
                                                >
                                            <span>
                                                {documentDto.documentTypeName === "Temporary Signature" ?
                                                    "Signature" : documentDto.documentTypeName}

                                                {
                                                    COMPARE_APPLICABLE_DOCS.includes(documentDto.captured.documentTypeCode) ?
                                                        <Button className={'doc-compare-btn'} type='link'
                                                                onClick={() => onCompare(
                                                                    documentDto.uploaded.uploadDocumentDetailDto,
                                                                    documentDto.captured.uploadDocumentDetailDto, documentDto.documentTypeName)}
                                                                disabled={documentDto.uploaded.uploadDocumentDetailDto === null || documentDto.captured.uploadDocumentDetailDto === null}
                                                        >Compare</Button> : ''
                                                }


                                                <></>
                                            </span>

                                                </Divider>
                                            </Col> : ''
                                    }
                                    <Col sm={12} xs={12}>
                                        {
                                            documentDto.uploaded.uploadDocumentDetailDto !== null ?
                                                <>
                                                    {getCompareModelBody(documentDto.uploaded, false)}

                                                </> : <>
                                                    <Alert
                                                        className={'captured-not-available-info'}
                                                        description="Not Available"
                                                        type="info"
                                                        // showIcon
                                                    />
                                                </>
                                        }
                                    </Col>
                                    <Col sm={12} xs={12}>
                                        {
                                            documentDto.captured.uploadDocumentDetailDto !== null ?
                                                <>
                                                    {getCompareModelBody(documentDto.captured, true)}

                                                </> :
                                                <>
                                                    {/*<Badge.Ribbon text={"Captured"}*/}
                                                    {/*              color={"#73d13d"}*/}

                                                    {/*              className={'doc-ribbon'}>*/}
                                                    {
                                                        COMPARE_APPLICABLE_DOCS.includes(documentDto.captured.documentTypeCode) ?
                                                            // <Alert
                                                            //     className={'captured-not-available-info'}
                                                            //     description="Not Available"
                                                            //     type="info"
                                                            //     // showIcon
                                                            // />

                                                            <Form.Item
                                                                name={"frontNicUpload"}
                                                                getValueFromEvent={normFile}
                                                                // initialValue={capturedDocumentList && capturedDocumentList?.length > 0 ? [capturedDocumentList] : undefined}
                                                                rules={[{
                                                                    required: true,
                                                                    message: 'Please upload the front image of the NIC'
                                                                }]}>

                                                                <Upload
                                                                    {...configs}
                                                                    accept={ALLOWED_IMAGE_TYPE_EXTENSION}
                                                                    className='upload_comp new-doc-upload-btn'
                                                                    listType="picture"
                                                                    fileList={capturedDocumentList && capturedDocumentList?.length > 0 ? capturedDocumentList : undefined}
                                                                    beforeUpload={(e: any) => beforeUploadNicFront(e, documentDto.captured.documentTypeId)}>
                                                                    {(undefined != capturedDocumentList ? capturedDocumentList.length : [].length) < 1 &&
                                                                        <Button>
                                                                            <div className='upload-btn-div'>
                                                                                <div className="upload-icon-div">
                                                                                    <PlusOutlined
                                                                                        style={{
                                                                                            fontSize: 20,
                                                                                            color: '#757575'
                                                                                        }}/>
                                                                                </div>
                                                                                <div className="upload-btn-text-div">
                                                                        <span>
                                                                            Upload Image {ALLOWED_IMAGE_TYPES_TEXT}
                                                                        </span>
                                                                                </div>
                                                                            </div>
                                                                        </Button>
                                                                    }
                                                                </Upload>

                                                            </Form.Item>
                                                            : ''}
                                                    {/*</Badge.Ribbon>*/}
                                                </>
                                        }
                                    </Col>
                                </Row>
                            )
                        })
                    }
                </div>
            </Spin>

            <Modal centered
                   open={open}
                   className={docType == "PDF" ? "modal-custom-bg" : "image-view-video-verification-modal"}
                   title={
                       <div
                           style={{
                               width: '100%',
                               cursor: 'move',
                           }}
                           onMouseOver={() => {
                               if (disabled) {
                                   setDisabled(false);
                               }
                           }}
                           onMouseOut={() => {
                               setDisabled(true);
                           }}

                       >
                           {docName}
                       </div>
                   }

                   onCancel={handleCancel}
                   destroyOnClose={true}
                   width={docType == "PDF" ? 800 : 480}
                   onMouseOver={() => {
                       if (disabled) {
                           setDisabled(false);
                       }
                   }}
                   onMouseOut={() => {
                       setDisabled(true);
                   }}
                   modalRender={(modal: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined) => (
                       <Draggable
                           disabled={disabled}
                           bounds={bounds}
                           nodeRef={draggleRef}
                           onStart={(event, uiData) => onStart(event, uiData)}
                       >
                           <div ref={draggleRef}>{modal}</div>
                       </Draggable>
                   )}
                   footer={[
                       <Button type="primary" icon={<DownloadOutlined/>}
                               style={{background: '#FF7400', height: 40}} onClick={download}>
                           Download
                       </Button>]}
            >
                <PreviewModalComp docURL={docUrl}/>
            </Modal>
            {/*Document Compare Modal*/}
            <Modal centered
                   style={{minWidth: 750}}
                   onOk={() => setCompareModalOpen(false)}
                   onCancel={() => setCompareModalOpen(false)}
                   destroyOnClose={true}
                   footer={[
                       <Button key="submit" type="primary" style={{width: 80, marginTop: 8}}
                               onClick={() => setCompareModalOpen(false)}>
                           Ok
                       </Button>]}
                   title={
                       <div
                           style={{
                               width: '100%',
                               cursor: 'move',
                           }}
                           onMouseOver={() => {
                               if (disabled) {
                                   setDisabled(false);
                               }
                           }}
                           onMouseOut={() => {
                               setDisabled(true);
                           }}

                       >
                           {`${compareModalDetails.documentType} Compare`}
                       </div>
                   }
                   open={isCompareModalOpen}

                   modalRender={(modal: string | number | boolean | ReactElement<any, string | JSXElementConstructor<any>> | Iterable<ReactNode> | ReactPortal | null | undefined) => (
                       <Draggable
                           disabled={disabled}
                           bounds={bounds}
                           nodeRef={draggleRef}
                           onStart={(event, uiData) => onStart(event, uiData)}
                       >
                           <div ref={draggleRef}>{modal}</div>
                       </Draggable>
                   )}
            >
                <Row gutter={[16, 16]} className={'compare-card'}>
                    <Col span={24}>
                        <div style={{borderTop: '1px solid #f0f0f0', marginLeft: '-24px', marginRight: '-24px'}}></div>
                    </Col>
                    <Col sm={12} md={12} lg={12}>
                        <Card size="small" title={`Customer Uploaded ${compareModalDetails.documentType}`}>
                            <PreviewModalComp docURL={compareModalDetails.customerUploadedDocUrl}/>
                        </Card>
                    </Col>
                    <Col sm={12} md={12} lg={12}>
                        <Card size="small" title={`Captured ${compareModalDetails.documentType}`}>
                            <PreviewModalComp docURL={compareModalDetails.capturedDocUrl}/>
                        </Card>
                    </Col>
                </Row>
            </Modal>


        </>);
    });

export default DocumentView;